import React, { PureComponent } from 'react';
import page from 'page';
import { connect } from 'react-redux';

import GlobalLayout from '../../layouts/GlobalLayout';
import MainLayout from '../../layouts/main-layout/MainLayout';
import MaintenanceLayout from '../../layouts/MaintenanceLayout';
import LazyComponent from '../LazyComponent';
import RouterTutorialHandler from 'modules/help-menu/helpers/RouterTutorialHandler';

import { askUserToConfirmRouteExit } from './confirm-exit';

import { Querystring } from 'utils/url';
import { PAGE_TYPE, shouldRedirectToAppStorePage } from 'utils/routes';

import {
  Action,
  GenericAnyCallback,
  GenericUnusedParams,
  LocationDeprecated
} from 'types';

type Route = {
  url: string,
  component?: React.JSX.Element,
  pageType?: string,
  onEnter?: (store: any, params: GenericUnusedParams, cb: GenericAnyCallback) => void,
  onLazyImport?: () => void,
  onLazyInit?: () => void
};

type Redirect = {
  from: string,
  to: string
};

type Props = {
  store?: any,
  routes?: Array<Route>,
  redirects?: Array<Redirect>,
  showMaintenancePage?: boolean,
  dispatch: (action: Action) => void,
  onUpdate?: (location: LocationDeprecated, prevLocation?: LocationDeprecated) => void
};

type State = {
  isLoading: boolean,
  showMaintenancePage: boolean,
  location?: LocationDeprecated,
  component?: React.JSX.Element,
  pageType?: string,
  params?: { [x: string]: any }
}

declare global {
  interface Window {
    currentLocation: LocationDeprecated
  }
}

class RouterProvider extends PureComponent<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      isLoading: true,
      showMaintenancePage: false
    };
  }

  componentDidMount() {
    this.initializeRedirects();
    this.initializeRoutes();

    page.start();

    const { onUpdate } = this.props;

    if (onUpdate && window.currentLocation) {
      onUpdate(window.currentLocation);
    }

    window.addEventListener('beforeunload', this.handleBeforeWindowUnload);
  }

  componentDidUpdate(prevProps, prevState) {
    const { onUpdate, showMaintenancePage: showMaintenancePageInProps } = this.props;
    const { location, showMaintenancePage: showMaintenancePageInState } = this.state;

    if (onUpdate && location) {
      onUpdate(location, prevState.location);
    }

    if (showMaintenancePageInState && !showMaintenancePageInProps) {
      this.setState({ // eslint-disable-line react/no-did-update-set-state
        showMaintenancePage: false
      });
    }

    if (!showMaintenancePageInState && showMaintenancePageInProps) {
      this.setState({ // eslint-disable-line react/no-did-update-set-state
        showMaintenancePage: true
      });
    }
  }

  componentWillUnmount(): void {
    window.removeEventListener('beforeunload', this.handleBeforeWindowUnload);
  }

  initializeRedirects() {
    const {
      redirects
    } = this.props;

    (redirects || []).forEach((redirect) => {
      page(redirect.from, (ctx) => {
        let urlTo = redirect.to;

        Object.keys(ctx.params).forEach((paramKey) => {
          urlTo = urlTo.replace(`:${paramKey}`, ctx.params[paramKey]);
        });

        // by documentation this is need it
        setTimeout(() => {
          if (!(ctx.querystring || '').includes('mobileRedirect=true')) {
            page.replace(urlTo);
          } else {
            page.replace('/app-store');
          }
        }, 0);
      });
    });
  }

  initializeRoutes() {
    const {
      routes,
      store
    } = this.props;

    (routes || []).forEach((route) => {
      const {
        url,
        pageType,
        onEnter,
        onLazyImport,
        onLazyInit,
        component: StaticComponent
      } = route;

      const Component = onLazyImport ?
        (props) =>
          <LazyComponent
            {...props}
            onLazyImport={onLazyImport}
            onLazyInit={onLazyInit}
          /> :
        StaticComponent;

      page(
        url,
        (ctx, next) => {
          if (window.currentLocation && window.currentLocation.path === ctx.path) {
            // ignore the same path (triggered by `page.replace` in `handleAskUserToConfirmRouteExit`)
            next();
            return;
          }

          this.handleAskUserToConfirmRouteExit(next);
        },
        (ctx) => {
          if (window.currentLocation && window.currentLocation.path === ctx.path) {
            // ignore the same path (triggered by `page.replace` in `handleAskUserToConfirmRouteExit`)
            return;
          }

          // remove the forward double slash
          if (ctx.path[1] === '/') {
            page.redirect(ctx.path.substring(1));
            return;
          }

          const location = {
            path: ctx.path,
            pathname: ctx.pathname,
            querystring: ctx.querystring,
            params: ctx.params,
            query: Querystring.stringToObject(decodeURIComponent(ctx.querystring))
          };

          window.currentLocation = location;

          const component = (
            // @ts-ignore - I struggle with types for that right now
            <Component
              params={ctx.params}
              location={location}
            />
          );

          if (onEnter) {
            onEnter(store, ctx.params, () => {
              this.handleUpdateState(ctx.params, pageType, component, location);
            });
          } else {
            this.handleUpdateState(ctx.params, pageType, component, location);
          }
        }
      );
    });
  }

  handleUpdateState = (params, pageType, component, location) => {
    if (window.currentLocation !== location) {
      // ignore if it's not the latest update
      return;
    }

    const { showMaintenancePage } = this.props;

    this.setState({
      isLoading: false,
      showMaintenancePage: Boolean(showMaintenancePage),
      params,
      pageType,
      component,
      location
    });
  };

  handleBeforeWindowUnload = (event): string | undefined | null => {
    const prompts = document.querySelectorAll('[class*="js-prompt-exit"]'); // page or modal
    const prompt = prompts.length > 0 ? prompts[prompts.length - 1] : null;
    let dialogText;

    if (prompt && prompt instanceof HTMLElement) {
      // modern browsers don't show a custom message but let's still keep it specified
      dialogText = `${decodeURIComponent(prompt.dataset.title ?? '')} ${decodeURIComponent(prompt.dataset.message ?? '')}`;
      event.returnValue = dialogText; // eslint-disable-line no-param-reassign
    }

    return dialogText;
  };

  handleAskUserToConfirmRouteExit(next): void {
    const { dispatch } = this.props;
    askUserToConfirmRouteExit(dispatch, next, () => {
      if (window.currentLocation) {
        // we have to revert URL change which is happening anyway
        setTimeout(() => page.replace(window.currentLocation.path), 0);
      }
    });
  }

  render() {
    const {
      component,
      pageType,
      params,
      isLoading,
      showMaintenancePage,
      location
    } = this.state;

    if (isLoading) {
      return null;
    }

    if (showMaintenancePage) {
      return (
        <MaintenanceLayout />
      );
    }

    const showFooter = pageType !== 'empty' && pageType !== PAGE_TYPE.CONSUMER_INSIGHTS;

    return (
      <RouterTutorialHandler location={location as LocationDeprecated}>
        <GlobalLayout showFooter={showFooter}>
          <MainLayout
            pageType={pageType}
            params={params}
            shouldRedirectToAppStorePage={shouldRedirectToAppStorePage(location as LocationDeprecated)}
          >
            {component}
          </MainLayout>
        </GlobalLayout>
      </RouterTutorialHandler>
    );
  }
}

const mapStateToProps = ({ runtimeConfig }) => ({
  showMaintenancePage: runtimeConfig.config.showMaintenancePage || false
});

export default connect(mapStateToProps)(RouterProvider);
